import { Plus, SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import { DataTable } from "@/components/ui/v2/table/data-table";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";
import { useUsers } from "@/features/users/api/useUsers";
import { useUserTableColumns } from "@/features/users/hooks/useUserTableColumns";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { UserRole, UserType } from "@/types/user/userType";

const OverviewUsersPage = () => {
  const { orderBy, descending } = useSortSearchParams();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { tenantId, context } = useAuth();
  const { clientId } = useParams();
  const { t } = useTranslation();
  const { canManageRole } = usePermissionMatrix();

  const isTenantUsersPage = !clientId && context === "admin";
  const canAddUser = isTenantUsersPage
    ? canManageRole(UserRole.ADMIN)
    : canManageRole(UserRole.SPECTATOR);

  const resolvedTenantId = !clientId && context === "admin" ? tenantId : null;

  const {
    data: users,
    isLoading: usersLoading,
    isFetching: usersFetching,
  } = useUsers({
    descending,
    orderBy,
    page,
    pageSize,
    search: debouncedSearchValue,
    tenantId: resolvedTenantId,
    clientId: clientId || null,
  });

  const { userTableColumns, deleteDialog } = useUserTableColumns();
  const handleSort = useTableSortHandler<UserType>();

  // Determine the base path for navigation
  const basePath = clientId
    ? `/${ROUTES.clients}/${clientId}/${ROUTES.users}`
    : `/${ROUTES.users}`;

  const titleKey =
    context === "admin"
      ? "admin.userManagement.title"
      : "client.userManagement.title";
  const subtitleKey =
    context === "admin"
      ? "admin.userManagement.description"
      : "client.userManagement.description";

  return (
    <ModernPageLayout title={t(titleKey)} subtitle={t(subtitleKey)}>
      <div className="flex flex-col gap-8">
        <TableToolbar
          onSearchChange={setSearchValue}
          searchValue={searchValue}
          actions={
            canAddUser ? (
              <Button asChild variant="default">
                <Link to={`${basePath}/${ROUTES.new}`}>
                  <Plus className="h-4 w-4" />
                  {t("common.add")} {t("common.user")}
                </Link>
              </Button>
            ) : null
          }
        />

        <DataTable
          columns={userTableColumns}
          data={users?.data}
          isLoading={usersLoading || usersFetching}
          orderBy={orderBy}
          descending={descending}
          onSort={handleSort}
          page={page}
          pageSize={pageSize}
          totalRecords={users?.totalRecords ?? 0}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          getRowId={(user) => user.id}
          emptyState={{
            icon: SearchX,
            title: t("common.noUsers", "No users found"),
            description: t(
              "common.usersNoDataDescription",
              "Get started by adding a user",
            ),
            ...(canAddUser && {
              action: {
                label: t("common.add") + " " + t("common.user"),
                href: `${basePath}/${ROUTES.new}`,
                icon: <Plus className="size-4 mr-2" />,
              },
            }),
          }}
        />
      </div>
      {deleteDialog}
    </ModernPageLayout>
  );
};

export default OverviewUsersPage;
